import { useEffect } from 'react';
import { useForm, useWatch, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ENTITLEMENT_KEYS, TIER_DEFAULTS, type EntitlementKey, type EntitlementValue } from '@kora/types';
import { PlatformModal } from './PlatformModal.js';
import type { EntitlementView } from '../../api/platform.js';

/**
 * Override-entitlement modal (Phase D-3). Mirrors the server Zod in
 * apps/api/src/routes/platform/subscriptions.ts (override-entitlement):
 *   - key (from the 13-key catalog),
 *   - value — adapts to the key's type: a boolean toggle for feature flags, a
 *     numeric input + "Unlimited" checkbox (→ null) for quotas (max_employees),
 *   - required audit reason.
 *
 * The live "default → override" preview is isolated into a useWatch child so the
 * numeric text field doesn't drop characters under userEvent (D-2 JC #5). The
 * numeric field is type="text" inputMode="numeric" for the same jsdom reason
 * (D-1 JC).
 */

/** A key is numeric if any tier default for it is a number (today: max_employees). */
export function isNumericKey(key: EntitlementKey): boolean {
  return (
    typeof TIER_DEFAULTS.starter[key] === 'number' ||
    typeof TIER_DEFAULTS.growth[key] === 'number' ||
    typeof TIER_DEFAULTS.enterprise[key] === 'number'
  );
}

function fmtValue(v: EntitlementValue): string {
  if (v === null) return 'unlimited';
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  return String(v);
}

const schema = z
  .object({
    key: z.enum(ENTITLEMENT_KEYS),
    boolValue: z.boolean(),
    numValue: z.string(),
    unlimited: z.boolean(),
    reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
  })
  .superRefine((v, ctx) => {
    if (isNumericKey(v.key) && !v.unlimited) {
      if (!/^\d+$/.test(v.numValue.trim())) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['numValue'], message: 'Enter a whole number (or check Unlimited)' });
      }
    }
  });
type Values = z.infer<typeof schema>;

/** Live preview of "tier default → new value", isolated to its own re-renders. */
function PreviewLine({ control, view }: { control: Control<Values>; view: EntitlementView }): JSX.Element {
  const key = useWatch({ control, name: 'key' });
  const boolValue = useWatch({ control, name: 'boolValue' });
  const numValue = useWatch({ control, name: 'numValue' });
  const unlimited = useWatch({ control, name: 'unlimited' });

  const def = view.tierDefaults[key];
  const next: EntitlementValue = isNumericKey(key)
    ? unlimited
      ? null
      : numValue.trim() === '' || !/^\d+$/.test(numValue.trim())
        ? NaN // shown as "—" below
        : Number(numValue)
    : boolValue;

  return (
    <p className="mt-2 text-sm text-gray-600" aria-label="override preview">
      Tier default: <span className="font-medium">{fmtValue(def)}</span> → New:{' '}
      <span className="font-medium">{typeof next === 'number' && Number.isNaN(next) ? '—' : fmtValue(next)}</span>
    </p>
  );
}

export function OverrideEntitlementModal({
  open,
  onClose,
  tenantName,
  view,
  initialKey,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  tenantName: string;
  view: EntitlementView;
  /** Pre-select a key (from the "Edit" action on a row). */
  initialKey?: EntitlementKey;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (body: { key: EntitlementKey; value: EntitlementValue; reason: string }) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { key: initialKey ?? ENTITLEMENT_KEYS[0], boolValue: false, numValue: '', unlimited: false, reason: '' },
  });

  // Re-seed the form each time the modal opens (pre-populate from the current
  // effective value for the chosen key).
  useEffect(() => {
    if (!open) return;
    const key = initialKey ?? ENTITLEMENT_KEYS[0];
    const eff = view.effectiveEntitlements[key];
    reset({
      key,
      boolValue: typeof eff === 'boolean' ? eff : false,
      numValue: typeof eff === 'number' ? String(eff) : '',
      unlimited: isNumericKey(key) && eff === null,
      reason: '',
    });
  }, [open, initialKey, view, reset]);

  const selectedKey = watch('key');
  const numeric = isNumericKey(selectedKey);

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const submit = (v: Values): void => {
    const value: EntitlementValue = isNumericKey(v.key)
      ? v.unlimited
        ? null
        : Number(v.numValue)
      : v.boolValue;
    void onConfirm({ key: v.key, value, reason: v.reason });
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title="Override entitlement"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="override-entitlement-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving…' : 'Save override'}
          </button>
        </>
      }
    >
      <form id="override-entitlement-form" onSubmit={handleSubmit(submit)}>
        <p className="text-sm text-gray-700 mb-3">
          Override an entitlement for <span className="font-medium">{tenantName}</span>. The value replaces the tier
          default for this key.
        </p>

        <label htmlFor="entitlement-key" className="block text-sm font-medium text-gray-700 mb-1">
          Entitlement
        </label>
        <select
          id="entitlement-key"
          {...register('key')}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        >
          {ENTITLEMENT_KEYS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>

        {numeric ? (
          <div className="mt-3">
            <label htmlFor="entitlement-num" className="block text-sm font-medium text-gray-700 mb-1">
              Value
            </label>
            <input
              id="entitlement-num"
              type="text"
              inputMode="numeric"
              {...register('numValue')}
              disabled={isSubmitting || watch('unlimited')}
              placeholder="e.g. 200"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary disabled:bg-gray-50"
            />
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...register('unlimited')} disabled={isSubmitting} />
              Unlimited (no cap)
            </label>
            {errors.numValue ? <p className="mt-1 text-sm text-red-600">{errors.numValue.message}</p> : null}
          </div>
        ) : (
          <label className="mt-3 flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" {...register('boolValue')} disabled={isSubmitting} aria-label="Enabled" />
            Enabled
          </label>
        )}

        <PreviewLine control={control} view={view} />

        <label htmlFor="entitlement-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="entitlement-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={2}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
